import { action, computed, observable } from 'mobx';
// import { showInfoToast } from 'core/components';
import { api } from 'core/util';
import JourneyCollection from 'app/views/core/journeys/JourneyCollection';
import ChatSessionCollection from 'app/views/core/kaizen/models/ChatSessionCollection';
import { uuidv7 } from 'uuidv7';

// const supportsNotifications =
//   typeof window !== 'undefined' && 'Notification' in window && typeof Notification.requestPermission === 'function';

class JourneysStore {
  // Journeys v1
  collection = new JourneyCollection();

  // Journeys v2 chat sessions
  chatSessionCollection = new ChatSessionCollection();

  // This is used to set the context for the copilot chat
  // Consumers that want to set the context for the copilot chat can call
  // $journeys.setChatContext(context)
  // where context is an object with the following properties:
  // - type: string (e.g. 'device', 'interface', 'cloud vpc')
  // - name: string (e.g. 'agg203.kentik.com')
  // - placeholder: string (e.g. 'Ask about this device')
  // - context: string (e.g. 'Full device details: {...}',
  @observable
  activeChatContext = new Set();

  socket = null;

  @observable
  isCopilotOpen = false;

  @observable
  copilotDimensions = {};

  @computed
  get isEnabled() {
    const hasJourneysV2 = this.store?.$auth.hasPermission('kaizen', { overrideForSudo: false });
    const hasAi = !!this.store?.$companySettings?.enableKentikAi;
    return hasJourneysV2 && hasAi;
  }

  @action
  openCopilot(options = {}) {
    const { isHalfScreen = false } = options;
    if (!this.isEnabled) {
      console.warn('Copilot is not enabled for this user/company');
      return;
    }

    this.isCopilotOpen = true;
    if (isHalfScreen) {
      this.copilotDimensions = {
        width: '50vw',
        height: '90vh'
      };
    }
  }

  @action
  clearCopilotDimensions() {
    this.copilotDimensions = {};
  }

  @action
  closeCopilot() {
    this.minimizeCopilot();
    this.chatSessionCollection.clearSelection();
    this.activeChatContext.clear();
  }

  @action
  minimizeCopilot() {
    this.isCopilotOpen = false;
  }

  @action
  toggleCopilot() {
    this.isCopilotOpen = !this.isCopilotOpen;
  }

  sendMessage(message, options = {}) {
    const { createNewJourney = false } = options;
    const prompt = message.trim();
    const context = this.serializeChatContext();

    // Create new journey if none selected
    if (!this.chatSessionCollection?.selected || createNewJourney) {
      const newJourney = this.createJourney();
      newJourney.startChatSessionAndAddPrompt(prompt, context);
    }

    // Add prompt to existing session
    else {
      const selectedSession = this.chatSessionCollection.selected;
      selectedSession.addPrompt(prompt, context);
    }
  }

  @action
  updateChatContext = (context) => {
    // Adds or replaces context by type. Use when you want only one context per unique type.
    // Find and remove old context by type
    const oldContext = Array.from(this.activeChatContext).find((ctx) => ctx.type === context.type);
    if (oldContext) {
      this.activeChatContext.delete(oldContext);
    }

    // Add the new context
    this.activeChatContext.add(context);
  };

  @action
  addChatContext = (context) => {
    if (!this.isEnabled) {
      console.warn('Journeys/Copilot is not enabled, cannot add chat context');
      return;
    }
    // Check if context with this name already exists
    const existingContext = Array.from(this.activeChatContext).find((ctx) => ctx.name === context.name);

    if (existingContext) {
      console.info('Context with name already exists, replacing:', context.name);
      this.activeChatContext.delete(existingContext);
    }

    this.activeChatContext.add(context);
  };

  @action
  clearAllChatContext = () => {
    this.activeChatContext.clear();
  };

  @action
  removeChatContext = (context) => {
    const contextToRemove = Array.from(this.activeChatContext).find((ctx) => ctx.name === context.name);
    if (contextToRemove) {
      this.activeChatContext.delete(contextToRemove);
    }
  };

  serializeChatContext() {
    if (this.activeChatContext.size === 0) {
      return '';
    }

    return Array.from(this.activeChatContext)
      .map((context) => {
        // If simple flag is set, just return the context without type/name prefix
        if (context.simple) {
          return context.context;
        }
        return `${context.type}: ${context.name} - ${context.context}`;
      })
      .join('\n');
  }

  // For testing purposes - can be called from browser console
  // e.g. window.$store.$journeys.testCopilot()
  testCopilot() {
    this.toggleCopilot();
  }

  get baseUrl() {
    return '/v4/core/ai-advisor';
  }

  @action
  createJourney = () => {
    // initializing with id here so it can be hashed when adding to collection
    const newJourney = this.chatSessionCollection.forge({ id: uuidv7() });
    this.chatSessionCollection.add(newJourney);
    return newJourney;
  };

  @action
  initialize() {
    // if (supportsNotifications) {
    //   Notification.requestPermission();
    //   this.socket = new Socket({
    //     outType: 'journeysNotifications',
    //     inType: 'journeysNotifications',
    //     frequency: 60,
    //     delaySend: true,
    //     listenToBroadcast: true,
    //     onSuccess: this.onJourneyNotification,
    //     onError(err) {
    //       console.warn('Received Journeys Notifications Socket Error', err);
    //     },
    //     onReconnect: () => {
    //       this.socket.send();
    //     }
    //   });
    //   this.socket.send();
    // }
  }

  // onJourneyNotification = (data) => {
  //   console.info('Received Journeys Notification', data);

  //   let notification;

  //   const [journey] = this.collection.add({ id: data.id });

  //   const onClick = () => {
  //     const { id } = data;

  //     this.history.push(`/v4/core/journeys/${id}`);
  //     if (notification) {
  //       notification.close();
  //     }
  //   };

  //   journey.fetch().then(() => {
  //     if (Notification.permission === 'granted') {
  //       console.info('Sending browser notification');
  //       notification = new Notification(data.title, {
  //         icon: 'https://images.ctfassets.net/6yom6slo28h2/33eo83sVM9DGq3WCFA5Uh5/70df4c8ad0906763e6a4d174cef5652c/kentik-ai-badge.png',
  //         body: data.description
  //       });

  //       this.store.$basicNotifications.addNotification({
  //         id: data.id,
  //         path: `/v4/core/journeys/${data.id}`,
  //         message: data.description,
  //         title: data.title,
  //         timestamp: new Date()
  //       });

  //       notification.onclick = onClick;
  //     } else {
  //       console.info('Showing info toast');
  //       showInfoToast(data.description, { title: data.title, action: { onClick, text: 'Launch Journey' } });
  //     }
  //   });
  // };

  startInvestigation(data) {
    return api.post('/api/ui/journeys/investigation/start', { data });
  }

  continueInvestigation(data) {
    return api.post('/api/ui/journeys/investigation/continue', { data });
  }
}

export default new JourneysStore();
